import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Radio, Button, Spin, Empty, message } from "antd";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import {
  InterviewRoundStatusEnum,
  InterviewRoundStatusLabel,
  InterviewRoundStatusColor,
} from "Constants/Enums/InterviewRoundStatusEnum";
import { InterviewRoundTypeLabel } from "Constants/Enums/InterviewRoundTypeEnum";

// HR-private fields that MUST NOT be shown to the applicant:
// summary, notes, strengthsObserved, concernsObserved, rating

function ApplicantInterviewRounds({ jobApplicationId, isPostingFilled = false }) {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlots, setSelectedSlots] = useState({});
  const [submittingRoundId, setSubmittingRoundId] = useState(null);

  const fetchRounds = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        API.hiringInterviews.getApplicantRoundsByApplication(jobApplicationId)
      );
      setRounds(response.data || []);
    } catch (err) {
      console.error("Error fetching applicant rounds:", err);
      if (err.response?.status !== 404) {
        message.error("Could not load your interview rounds.");
      }
      setRounds([]);
    } finally {
      setLoading(false);
    }
  }, [jobApplicationId]);

  useEffect(() => {
    if (jobApplicationId) fetchRounds();
  }, [jobApplicationId, fetchRounds]);

  const handleSlotChange = (roundId, slotNumber) => {
    setSelectedSlots((prev) => ({ ...prev, [roundId]: slotNumber }));
  };

  const handleSlotSubmit = async (round) => {
    const slot = selectedSlots[round.id];
    if (!slot) {
      message.warning("Please choose one of the proposed time slots first.");
      return;
    }
    try {
      setSubmittingRoundId(round.id);
      // Per Swagger: field is `jobInterviewRoundId`, NOT `interviewRoundId`.
      await axiosInstance.post(API.hiringInterviews.selectSlot, {
        jobInterviewRoundId: round.id,
        selectedSlot: slot, // 1, 2, or 3
      });
      message.success("Your time slot has been submitted to HR.");
      await fetchRounds();
      setSelectedSlots((prev) => {
        const next = { ...prev };
        delete next[round.id];
        return next;
      });
    } catch (err) {
      console.error("Error selecting slot:", err);
      message.error(
        err.response?.data?.message ||
          "Failed to submit your slot selection. Please try again."
      );
    } finally {
      setSubmittingRoundId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "32px" }}>
        <Spin size="large" />
      </div>
    );
  }

  // Per Hammad: rounds named "Hiring Committee", "Final Decision", "Offer" are
  // decision/offer stages, NOT interview rounds — hide them from the candidate's
  // interview-rounds view entirely.
  const isDecisionStage = (r) => {
    const n = String(r.roundName || "").toLowerCase();
    return (
      n.includes("decision") ||
      n.includes("offer") ||
      n.includes("hiring committee")
    );
  };
  const interviewOnly = (rounds || []).filter((r) => !isDecisionStage(r));

  if (interviewOnly.length === 0) {
    return (
      <Empty
        description="Your interview rounds will appear here once HR sets them up."
        style={{ padding: "32px" }}
      />
    );
  }

  const completed = interviewOnly.filter(
    (r) => r.status === InterviewRoundStatusEnum.Completed
  ).length;
  const awaitingAction = interviewOnly.filter(
    (r) => r.status === InterviewRoundStatusEnum.SlotsProposed
  ).length;

  return (
    <div>
      {/* Header banner */}
      <div
        style={{
          background: "linear-gradient(135deg, #0f5a2a 0%, #1e8c45 100%)",
          borderRadius: "20px",
          padding: "18px 24px",
          color: "white",
          marginBottom: "20px",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          boxShadow: "0 8px 20px rgba(15,90,42,0.18)",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "0.72rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              opacity: 0.85,
              fontWeight: 600,
            }}
          >
            Your Interview Process
          </div>
          <div style={{ fontSize: "1.3rem", fontWeight: 600, marginTop: "4px" }}>
            🎯 {interviewOnly.length} round{interviewOnly.length === 1 ? "" : "s"} scheduled
          </div>
          {awaitingAction > 0 && (
            <div
              style={{
                fontSize: "0.85rem",
                opacity: 0.95,
                marginTop: "6px",
                color: "#ffd700",
              }}
            >
              ⚡ {awaitingAction} round{awaitingAction === 1 ? "" : "s"} need
              your time-slot selection below.
            </div>
          )}
        </div>
        <div
          style={{
            background: "rgba(255,255,255,0.12)",
            border: "1.5px solid rgba(255,255,255,0.25)",
            borderRadius: "16px",
            padding: "10px 18px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
              opacity: 0.85,
            }}
          >
            Completed
          </div>
          <div style={{ fontSize: "1.6rem", fontWeight: 700, lineHeight: 1.1 }}>
            {completed}{" "}
            <span style={{ opacity: 0.7, fontSize: "0.95rem" }}>
              / {interviewOnly.length}
            </span>
          </div>
        </div>
      </div>

      {/* Round list — interview rounds only (decision/offer stages filtered out) */}
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {interviewOnly.map((round) => (
          <RoundCard
            key={round.id}
            round={round}
            selectedSlot={selectedSlots[round.id]}
            onSlotChange={(slot) => handleSlotChange(round.id, slot)}
            // Per Mudassir: when the posting is Filled, applicants can no
            // longer confirm interview slots even if rounds remain in
            // SlotsProposed state.
            onSubmitSlot={
              isPostingFilled ? null : () => handleSlotSubmit(round)
            }
            submitting={submittingRoundId === round.id}
            disabled={isPostingFilled}
          />
        ))}
      </div>
    </div>
  );
}

ApplicantInterviewRounds.propTypes = {
  jobApplicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  isPostingFilled: PropTypes.bool,
};

function RoundCard({
  round,
  selectedSlot,
  onSlotChange,
  onSubmitSlot,
  submitting,
  disabled = false,
}) {
  const roundTypeLabel = InterviewRoundTypeLabel[round.roundType] ?? "—";
  const statusLabel = InterviewRoundStatusLabel[round.status] ?? "Unknown";
  const statusColor = InterviewRoundStatusColor[round.status] ?? "#8c8c8c";

  return (
    <div
      style={{
        background: "white",
        borderRadius: "20px",
        padding: "18px 22px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.03)",
        border: "1px solid #e9edf2",
        borderLeft: `5px solid ${statusColor}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: "10px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "#617e9e",
              fontWeight: 600,
            }}
          >
            Round {round.roundNumber || "—"}
          </div>
          <div
            style={{
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "#0f5a2a",
              marginTop: "2px",
            }}
          >
            {round.roundName || roundTypeLabel}
          </div>
          {round.roundName && (
            <div style={{ fontSize: "0.8rem", color: "#5b6f8c", marginTop: "2px" }}>
              🎯 Type: {roundTypeLabel}
            </div>
          )}
        </div>
        <span
          style={{
            background: statusColor,
            color: "#fff",
            padding: "5px 14px",
            borderRadius: "40px",
            fontSize: "0.72rem",
            fontWeight: 600,
            letterSpacing: "0.3px",
            textTransform: "uppercase",
          }}
        >
          {statusLabel}
        </span>
      </div>

      <RoundBody
        round={round}
        selectedSlot={selectedSlot}
        onSlotChange={onSlotChange}
        onSubmitSlot={onSubmitSlot}
        submitting={submitting}
        disabled={disabled}
      />
    </div>
  );
}
RoundCard.propTypes = {
  round: PropTypes.object.isRequired,
  selectedSlot: PropTypes.number,
  onSlotChange: PropTypes.func.isRequired,
  onSubmitSlot: PropTypes.func,
  submitting: PropTypes.bool,
  disabled: PropTypes.bool,
};

function RoundBody({
  round,
  selectedSlot,
  onSlotChange,
  onSubmitSlot,
  submitting,
  disabled = false,
}) {
  if (round.status === InterviewRoundStatusEnum.PendingSlotProposal) {
    return (
      <InfoBox
        bg="#fff7ed"
        accent="#f59e0b"
        color="#92400e"
        icon="⏳"
        text="HR is preparing time slots for this round. You'll see them here soon."
      />
    );
  }

  if (round.status === InterviewRoundStatusEnum.SlotsProposed) {
    const slots = [
      { num: 1, value: round.slotOption1 },
      { num: 2, value: round.slotOption2 },
      { num: 3, value: round.slotOption3 },
    ].filter((s) => s.value);

    return (
      <div>
        <div
          style={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: "#1f3a5f",
            marginBottom: "10px",
            textTransform: "uppercase",
            letterSpacing: "0.4px",
          }}
        >
          📅 Choose a time that works for you
        </div>

        {slots.length === 0 ? (
          <div style={{ color: "#8c8c8c", fontSize: "0.85rem" }}>
            No slots provided. Please contact HR.
          </div>
        ) : (
          <Radio.Group
            value={selectedSlot}
            onChange={(e) => onSlotChange(e.target.value)}
            style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}
          >
            {slots.map((s) => (
              <SlotOption
                key={s.num}
                num={s.num}
                value={s.value}
                checked={selectedSlot === s.num}
              />
            ))}
          </Radio.Group>
        )}

        <div style={{ marginTop: "14px", textAlign: "right" }}>
          <Button
            type="primary"
            size="middle"
            loading={submitting}
            disabled={!selectedSlot || slots.length === 0 || disabled}
            onClick={onSubmitSlot}
            style={{
              background: "#0f5a2a",
              borderColor: "#0f5a2a",
              borderRadius: "40px",
              fontWeight: 600,
              paddingLeft: "22px",
              paddingRight: "22px",
            }}
          >
            {submitting ? "Submitting..." : "Confirm slot"}
          </Button>
        </div>
      </div>
    );
  }

  if (round.status === InterviewRoundStatusEnum.SlotSelected) {
    return (
      <InfoBox
        bg="#f5f3ff"
        accent="#7c3aed"
        color="#5b21b6"
        icon="📅"
        text={
          <>
            <strong>Confirmed for:</strong>{" "}
            {formatDateTime(round.selectedDateTime)}.{" "}
            <span style={{ opacity: 0.9 }}>
              {"We'll be in touch closer to the date."}
            </span>
          </>
        }
      />
    );
  }

  if (round.status === InterviewRoundStatusEnum.Completed) {
    return (
      <InfoBox
        bg="#f0fdf4"
        accent="#22c55e"
        color="#166534"
        icon="✅"
        text={
          <>
            This round was completed
            {round.selectedDateTime && (
              <> on <strong>{formatDateTime(round.selectedDateTime)}</strong></>
            )}
            . Thank you for your time!
          </>
        }
      />
    );
  }

  if (round.status === InterviewRoundStatusEnum.Cancelled) {
    return (
      <InfoBox
        bg="#fef2f2"
        accent="#ef4444"
        color="#991b1b"
        icon="✗"
        text="This round has been cancelled by HR."
      />
    );
  }

  if (round.status === InterviewRoundStatusEnum.Skipped) {
    return (
      <InfoBox
        bg="#f5f5f5"
        accent="#8c8c8c"
        color="#5b6f8c"
        icon="⏭"
        text="This round was skipped."
      />
    );
  }

  return null;
}
RoundBody.propTypes = {
  round: PropTypes.object.isRequired,
  selectedSlot: PropTypes.number,
  onSlotChange: PropTypes.func.isRequired,
  onSubmitSlot: PropTypes.func,
  submitting: PropTypes.bool,
  disabled: PropTypes.bool,
};

function SlotOption({ num, value, checked }) {
  return (
    <Radio
      value={num}
      style={{
        width: "100%",
        padding: "10px 14px",
        background: checked ? "#e6f4ea" : "#fafdfb",
        borderRadius: "14px",
        border: `1.5px solid ${checked ? "#0f5a2a" : "#e6ede6"}`,
        transition: "background 0.15s ease, border-color 0.15s ease",
        margin: 0,
      }}
    >
      <span style={{ marginLeft: "8px" }}>
        <span
          style={{
            display: "inline-block",
            background: "#0f5a2a",
            color: "white",
            padding: "2px 10px",
            borderRadius: "40px",
            fontSize: "0.7rem",
            fontWeight: 600,
            marginRight: "10px",
          }}
        >
          Option {num}
        </span>
        <span style={{ fontSize: "0.95rem", color: "#0f5a2a", fontWeight: 500 }}>
          {formatDateTime(value)}
        </span>
      </span>
    </Radio>
  );
}
SlotOption.propTypes = {
  num: PropTypes.number,
  value: PropTypes.string,
  checked: PropTypes.bool,
};

function InfoBox({ bg, accent, color, icon, text }) {
  return (
    <div
      style={{
        background: bg,
        borderRadius: "14px",
        padding: "12px 16px",
        borderLeft: `3px solid ${accent}`,
        color,
        fontSize: "0.9rem",
        lineHeight: 1.5,
      }}
    >
      <span style={{ marginRight: "8px" }}>{icon}</span>
      {text}
    </div>
  );
}
InfoBox.propTypes = {
  bg: PropTypes.string,
  accent: PropTypes.string,
  color: PropTypes.string,
  icon: PropTypes.string,
  text: PropTypes.node,
};

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

export default ApplicantInterviewRounds;
